'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { Button, LinkButton } from '@/components/ui/button'
import Section from '@/components/marketing/Section'
import Arrow from '@/components/marketing/Arrow'
import { cn } from '@/lib/utils'
import { marketingFieldClasses } from '@/components/marketing/Input'
import { getStoredAIReferrer } from '@/lib/analytics/ai-referrer-tracker'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import { getFirstTouchUtm } from '@/lib/analytics/first-touch-utm'
import { trackEvent } from '@/lib/track-event'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

/**
 * /contact-us — rebuilt 2026-08-08 from
 * public/pixelmojo-refactor/Contact Pixelmojo — Bring us the constraint.html,
 * adapted to our tokens and type classes.
 *
 * Sections, in the reference's order: hero + route board, the brief form
 * beside its aside, "what happens next", the fit split, FAQ, and the direct
 * strip.
 *
 * Everything behind the form is unchanged: the same /api/contact payload,
 * the same field names, the same consent-aware first-touch UTM handling, the
 * same contact_form_started / contact_form_submitted events, and the same
 * AI-referrer capture. Only the presentation and the option sets moved.
 */

// Stable identifier for this form instance. There is no service field on this
// form (only topic/budget), so contact_form_* events omit `service` rather
// than manufacture a value.
const FORM_ID = 'contact_us'

// The API caps `message` at 1000 characters (contactSchema). The counter and
// maxLength mirror the server so a long brief is trimmed while typing rather
// than rejected on submit — the reference's 1200 would have failed validation.
const MESSAGE_MAX = 1000

interface FormData {
  name: string
  email: string
  subject: string
  budget: string
  message: string
  aiReferrerSource: string
  aiReferrerURL: string
}

/**
 * The seven routes into the studio. `subject` is what reaches the API, where it
 * feeds lead scoring and the notification subject line, so the values here are
 * the readable labels the scoring table knows — not the reference's slugs.
 *
 * `context` takes the starting price so it can never drift from
 * services-knowledge.json; the two custom builds quote a duration instead,
 * matching what the Vector and Hive pages commit to.
 */
const INTENTS = [
  {
    subject: 'AI Visibility',
    title: 'AI visibility',
    kicker: 'Audit + implementation',
    priceKey: 'ai-visibility-strategy',
    context: (price: string) =>
      `Expert audit and implementation ${price}. Start with Radar if you have not run a baseline.`,
    placeholder:
      'What does AI currently get wrong, and what outcome would make this engagement worthwhile?',
  },
  {
    subject: 'AI Product',
    title: 'AI product',
    kicker: 'Concept to production',
    priceKey: 'ai-product-development',
    context: (price: string) =>
      `AI product development starts ${price}, with a typical 90-day launch window.`,
    placeholder:
      'What product are you building, who is it for, and what has already been validated?',
  },
  {
    subject: 'Vector',
    title: 'Vector',
    kicker: 'Lead qualification',
    priceKey: null,
    context: (_price: string) =>
      'Vector is a custom 60-day lead-qualification build. Your team owns the application code.',
    placeholder:
      'How do leads arrive today, what makes one qualified, and where does the current process break?',
  },
  {
    subject: 'Hive',
    title: 'Hive',
    kicker: 'AI co-workers',
    priceKey: null,
    context: (_price: string) =>
      'Hive is a custom 12-week multi-agent operations build. Your team owns the application code.',
    placeholder:
      'Which recurring operation should AI co-workers run, and which systems must they coordinate with?',
  },
  {
    subject: 'Brand + Sales',
    title: 'Brand + sales',
    kicker: 'Identity + enablement',
    priceKey: 'revenue-first-design',
    context: (price: string) =>
      `Brand and sales design starts ${price}, scoped as one coherent system.`,
    placeholder:
      'What needs to become clearer or more credible, and which sales or product surfaces matter most?',
  },
  {
    subject: 'Growth',
    title: 'Growth',
    kicker: 'Pipeline + automation',
    priceKey: 'ai-powered-growth',
    context: (price: string) =>
      `Growth engagements start ${price} across content, automation, and analytics.`,
    placeholder:
      'Where is pipeline breaking today, and which acquisition or nurture systems already exist?',
  },
  {
    subject: 'Partnership',
    title: 'Consulting',
    kicker: 'Agency partner delivery',
    priceKey: null,
    context: (_price: string) =>
      'For agencies and consultancies that keep the client relationship while Pixelmojo executes an agreed website, application, or AI visibility scope behind the scenes.',
    placeholder:
      'Tell us about your agency, the client situation, what you have already promised, and the delivery capacity you need.',
  },
] as const

const BUDGETS = [
  'Under $5,000',
  '$5,000 to $10,000',
  '$10,000 to $25,000',
  '$25,000 to $50,000',
  '$50,000+',
  'Not sure yet',
]

const PROMISES = [
  'Application code ownership',
  'No vendor lock-in',
  'One accountable team',
]

const NEXT_STEPS = [
  {
    number: '01 · Review',
    title: 'We read the context.',
    copy: 'We check the constraint, desired outcome, timing, and whether Pixelmojo is the right team.',
  },
  {
    number: '02 · Reply',
    title: 'You get a clear next step.',
    copy: 'Within one business day: fit, useful questions, and the right starting point, not an automated sales sequence.',
  },
  {
    number: '03 · Scope',
    title: 'One call, then a concrete plan.',
    copy: 'If there is a fit, we align on outcomes and return with scope, checkpoints, ownership, and timeline.',
  },
]

const STRONG_FIT = [
  'There is a real workflow, visibility gap, or product constraint to solve.',
  'You want production work, not a concept that stops at the presentation.',
  'Your team values clear checkpoints, evidence, and full ownership.',
  'You can share enough context to make good scoping possible.',
]

const START_SMALLER = [
  "Run Radar's six free technical checks first.",
  'Use the report to identify the actual constraint.',
  'Bring the baseline into an internal or agency conversation.',
  'Unlock all 13 tools from $5 only when the full answer matters.',
]

const FAQS = [
  {
    q: 'What should I include in the brief?',
    a: 'Start with the business problem, who experiences it, the current workflow, the result you want, and any known timing or system constraints. Confidential implementation details can wait.',
  },
  {
    q: 'What happens after I submit?',
    a: 'Pixelmojo reviews the context and replies within one business day with whether there is a fit, the most useful next question, and the recommended starting point. A scoped call follows only when it adds value.',
  },
  {
    q: 'Will we own the finished work?',
    a: 'Yes. Custom product engagements hand over the application code, assets, documentation, and systems created for the project. The goal is ownership, not dependency.',
  },
  {
    q: 'Can I start without a custom engagement?',
    a: "Yes. Radar's six technical-readiness checks are free and require no credit card. A full 13-tool audit starts from $5, and audit credits do not expire.",
  },
  {
    q: 'What if Pixelmojo is not the right fit?',
    a: 'The reply should say so plainly. A useful first conversation clarifies the path, even when that path is an internal fix, Radar alone, or a different specialist.',
  },
]

const defaultFormData: FormData = {
  name: '',
  email: '',
  subject: INTENTS[0].subject,
  budget: '',
  message: '',
  aiReferrerSource: '',
  aiReferrerURL: '',
}

const resolveRequestedIntent = (search: string): number | null => {
  const requestedIntent = new URLSearchParams(search).get('intent')
  if (requestedIntent !== 'partnership') return null

  const partnershipIndex = INTENTS.findIndex(
    option => option.subject === 'Partnership'
  )

  return partnershipIndex >= 0 ? partnershipIndex : null
}

export default function Contact({
  prices,
}: {
  /** Starting prices keyed by services-knowledge id, resolved on the server. */
  prices: Record<string, string>
}) {
  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [intent, setIntent] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [aiResponse, setAiResponse] = useState('')
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLParagraphElement>(null)
  const pathname = usePathname()
  // Fires contact_form_started at most once per rendered form instance, on the
  // first real edit (not viewport, not mount, not programmatic focus).
  const startedRef = useRef(false)

  // Move focus to the error so keyboard/screen-reader users notice a failed submit
  useEffect(() => {
    if (error) {
      errorRef.current?.focus()
    }
  }, [error])

  // Detect AI referrer on mount
  useEffect(() => {
    const aiReferrer = getStoredAIReferrer()
    if (aiReferrer?.isAIReferred) {
      setFormData(prev => ({
        ...prev,
        aiReferrerSource: aiReferrer.source,
        aiReferrerURL: aiReferrer.referrer,
      }))
    }
  }, [])

  // Partner-delivery CTAs preselect the existing Partnership subject without
  // counting a programmatic selection as a form start. Reading from `window`
  // keeps this client leaf out of Next's useSearchParams/Suspense boundary.
  useEffect(() => {
    const partnershipIndex = resolveRequestedIntent(window.location.search)
    if (partnershipIndex === null) return

    setIntent(partnershipIndex)
    setFormData(previous => ({
      ...previous,
      subject: INTENTS[partnershipIndex].subject,
    }))
  }, [])

  // Scroll to top when form is submitted
  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [submitted])

  const markStarted = () => {
    // First genuine interaction with an editable control → started (once).
    if (startedRef.current) return
    startedRef.current = true
    trackEvent('contact_form_started', { form_id: FORM_ID, page: pathname })
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    markStarted()
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const selectIntent = (index: number) => {
    markStarted()
    setIntent(index)
    setFormData(prev => ({ ...prev, subject: INTENTS[index].subject }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    // First-touch UTM (may be empty). Defense in depth: only read, forward, and
    // attach it when analytics are CURRENTLY allowed. getFirstTouchUtm() is
    // already consent-aware (returns null and clears stale data when
    // disallowed), but this explicit guard keeps a disallowed submission
    // UTM-free in BOTH the /api/contact payload and the PostHog event even if
    // that internal gate ever changed.
    const firstTouchUtm = isAnalyticsConsentGranted()
      ? (getFirstTouchUtm() ?? {})
      : {}

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // UTM fields are optional and backward-compatible; the API ignores
        // absent ones. No overwrite of first-touch happens here.
        // peek, never mint: a form submit must not create attribution
        // for a visitor who was never LLM-referred.
        body: JSON.stringify({
          ...formData,
          ...firstTouchUtm,
          visitorId: peekVisitorId(),
        }),
      })

      const data = (await response.json()) as {
        message?: string
        aiResponse?: string
      }

      if (!response.ok) {
        throw new Error(
          data.message || 'We could not send your message. Please try again.'
        )
      }

      // Confirmed successful submission (response.ok) → conversion event.
      // `service` is intentionally omitted (no service field on this form).
      // No PII: only a fixed form id, the pathname, and first-touch UTM.
      trackEvent('contact_form_submitted', {
        form_id: FORM_ID,
        page: pathname,
        ...firstTouchUtm,
      })

      // Track AI-referred form submission (consented users only)
      if (
        formData.aiReferrerSource &&
        isAnalyticsConsentGranted() &&
        window.gtag
      ) {
        window.gtag('event', 'ai_referred_form_submission', {
          ai_source: formData.aiReferrerSource,
          ai_referrer: formData.aiReferrerURL,
          event_category: 'agentic_commerce',
          event_label: `AI Form Submit: ${formData.aiReferrerSource}`,
          value: 1,
        })
      }

      setAiResponse(data.aiResponse || '')
      setSubmitted(true)
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : 'We could not send your message. Please try again.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const activeIntent = INTENTS[intent]
  const intentContext = activeIntent.priceKey
    ? activeIntent.context(prices[activeIntent.priceKey] ?? '')
    : activeIntent.context('')

  if (submitted) {
    return (
      <Section width='wide' innerClassName='animate-fade-in'>
        <div className='bg-card border border-line-strong p-8 md:p-[70px] min-h-[520px] grid content-center'>
          <span
            className='grid place-items-center w-16 h-16 mb-7 bg-[var(--cta-fill)] text-[var(--cta-foreground)] text-3xl font-extrabold'
            aria-hidden='true'
          >
            ✓
          </span>
          <span className='instrument-label block mb-5'>Brief ready</span>
          <h1 className='thesis-headline thesis-headline--category max-w-[650px]'>
            Thanks, {formData.name.split(' ')[0] || 'there'}. We have the
            context.
          </h1>
          <p className='section-lead max-w-[600px] mt-6'>
            You will get a reply within one business day with whether there is a
            fit and the right next step.
          </p>

          {aiResponse && (
            <div className='border border-line-strong bg-muted/50 p-6 mt-8 max-w-[600px]'>
              <div className='mb-4 flex items-center gap-3'>
                <span
                  aria-hidden='true'
                  className='flex h-8 w-8 items-center justify-center bg-primary/10 text-sm font-bold text-primary'
                >
                  V
                </span>
                <span className='instrument-label text-muted-foreground'>
                  Vector, Pixelmojo
                </span>
              </div>
              <p className='mb-0 text-foreground'>{aiResponse}</p>
            </div>
          )}

          <div className='mt-8'>
            <Button
              onClick={() => {
                const requestedIntent =
                  resolveRequestedIntent(window.location.search) ?? 0
                setSubmitted(false)
                setAiResponse('')
                setFormData({
                  ...defaultFormData,
                  subject: INTENTS[requestedIntent].subject,
                  aiReferrerSource: formData.aiReferrerSource,
                  aiReferrerURL: formData.aiReferrerURL,
                })
                setIntent(requestedIntent)
              }}
              variant='neutral'
            >
              Send another brief
            </Button>
          </div>
        </div>
      </Section>
    )
  }

  return (
    <>
      {/* ── Hero + route board */}
      <Section width='wide'>
        <div className='grid lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.72fr)] gap-12 lg:gap-[72px] lg:items-end'>
          <div>
            <span className='instrument-label block mb-5'>
              Contact · One business day
            </span>
            <h1 className='thesis-headline thesis-headline--category'>
              Bring us the constraint.{' '}
              <span className='text-cta'>Get a clear next move.</span>
            </h1>
            <p className='section-lead mt-7'>
              Tell us what needs to change: visibility, qualification,
              operations, product, brand, growth, or partner delivery. We will
              reply with whether we are a fit and the right next step.
            </p>
            <ul className='flex flex-wrap gap-x-[22px] gap-y-2.5 mt-8 list-none m-0 p-0'>
              {PROMISES.map(promise => (
                <li
                  key={promise}
                  className='instrument-label text-muted-foreground m-0'
                >
                  <span aria-hidden='true' className='text-cta mr-[7px]'>
                    ✓
                  </span>
                  {promise}
                </li>
              ))}
            </ul>
          </div>

          {/* .route-board — the four ways in, fused inside a bordered frame. */}
          <div
            className='p-2.5 border border-line-strong bg-card'
            aria-label='Choose how to start'
          >
            <div className='flex items-center justify-between gap-5 min-h-[42px] px-2 pb-2.5'>
              <span className='instrument-label text-muted-foreground'>
                Two ways in
              </span>
              <span className='instrument-label text-muted-foreground'>
                Reply within one business day
              </span>
            </div>
            <div className='grid grid-cols-1 gap-px bg-line sm:grid-cols-2'>
              <div className='relative min-h-[182px] p-[21px] bg-muted/40'>
                <span className='instrument-label text-muted-foreground block mb-[38px]'>
                  Need a baseline?
                </span>
                <strong className='card-headline card-headline--3xl block'>
                  Start with Radar.
                </strong>
                <p className='m-0 mt-2 text-xs leading-relaxed text-muted-foreground'>
                  Six technical checks. Free. Results start in 60 seconds.
                </p>
              </div>

              <a
                href='#contact-form'
                className='accent-surface group relative min-h-[182px] p-[21px]'
              >
                <span className='instrument-label block mb-[38px]'>
                  Know the constraint?
                </span>
                <strong className='card-headline card-headline--3xl block'>
                  Brief the studio.
                </strong>
                <p className='m-0 mt-2 text-xs leading-relaxed'>
                  Share the goal and current state.
                </p>
                <span
                  aria-hidden='true'
                  className='absolute right-4 bottom-3.5 instrument-label'
                >
                  ↓
                </span>
              </a>

              <Link
                href='/platform'
                className='group relative min-h-[182px] p-[21px] bg-muted/40 transition-colors hover:bg-muted/70'
              >
                <span className='instrument-label text-muted-foreground block mb-[38px]'>
                  Self-serve
                </span>
                <strong className='card-headline card-headline--3xl block'>
                  Run the audit now.
                </strong>
                <p className='m-0 mt-2 text-xs leading-relaxed text-muted-foreground'>
                  No call. No card. No waiting.
                </p>
                <span
                  aria-hidden='true'
                  className='absolute right-4 bottom-3.5 instrument-label'
                >
                  ↗
                </span>
              </Link>

              <a
                href='mailto:founders@pixelmojo.io'
                className='group relative min-h-[182px] p-[21px] bg-muted/40 transition-colors hover:bg-muted/70'
              >
                <span className='instrument-label text-muted-foreground block mb-[38px]'>
                  Prefer email?
                </span>
                <strong className='card-headline card-headline--3xl block break-words'>
                  founders@
                  <br />
                  pixelmojo.io
                </strong>
                <span
                  aria-hidden='true'
                  className='absolute right-4 bottom-3.5 instrument-label'
                >
                  ↗
                </span>
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ── The brief */}
      <div className='border-t border-line' id='contact-form'>
        <Section width='wide'>
          <div className='grid lg:grid-cols-[320px_1fr] gap-px border border-line-strong bg-line'>
            <aside className='flex flex-col justify-between p-[30px] bg-card'>
              <div>
                <span className='instrument-label block mb-5'>
                  Project brief
                </span>
                <h2 className='thesis-headline'>
                  Enough context to be useful.
                </h2>
                <p className='text-base leading-relaxed text-foreground-soft mt-5'>
                  The more specific the constraint, the more concrete our reply
                  can be.
                </p>

                <div className='grid gap-px mt-[38px] border border-line bg-line'>
                  <div className='p-[17px] bg-muted/40'>
                    <span className='instrument-label text-muted-foreground block'>
                      Response
                    </span>
                    <strong className='block mt-[7px] text-xs font-semibold'>
                      Within one business day
                    </strong>
                  </div>
                  <div className='p-[17px] bg-muted/40'>
                    <span className='instrument-label text-muted-foreground block'>
                      Direct email
                    </span>
                    <a
                      href='mailto:founders@pixelmojo.io'
                      className='block mt-[7px] text-xs hover:text-cta transition-colors'
                    >
                      founders@pixelmojo.io
                    </a>
                  </div>
                  <div className='p-[17px] bg-muted/40'>
                    <span className='instrument-label text-muted-foreground block'>
                      Studio
                    </span>
                    <strong className='block mt-[7px] text-xs font-semibold'>
                      Makati · Philippines
                    </strong>
                  </div>
                </div>
              </div>
              <p className='m-0 mt-[30px] text-xs leading-loose text-muted-foreground'>
                Confidential details can wait. Start with the business problem,
                current workflow, desired result, and known constraints.
              </p>
            </aside>

            <form
              onSubmit={handleSubmit}
              className='p-[clamp(30px,5vw,62px)] bg-muted/40'
            >
              <div className='flex items-start justify-between gap-6 mb-[38px]'>
                <div>
                  <span className='instrument-label text-muted-foreground block'>
                    One focused form
                  </span>
                  <h3 className='card-headline card-headline--4xl mt-2'>
                    What should change?
                  </h3>
                </div>
                <span className='instrument-label text-muted-foreground text-right shrink-0'>
                  Required fields marked *
                </span>
              </div>

              <div className='grid sm:grid-cols-2 gap-3.5'>
                <div className='grid gap-2'>
                  <label
                    htmlFor='name'
                    className='text-sm font-semibold text-foreground-soft'
                  >
                    Name *
                  </label>
                  <input
                    type='text'
                    id='name'
                    name='name'
                    required
                    autoComplete='name'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder='Jane Doe'
                    className={marketingFieldClasses({ size: 'lg' })}
                  />
                </div>
                <div className='grid gap-2'>
                  <label
                    htmlFor='email'
                    className='text-sm font-semibold text-foreground-soft'
                  >
                    Work email *
                  </label>
                  <input
                    type='email'
                    id='email'
                    name='email'
                    required
                    autoComplete='email'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder='jane@company.com'
                    className={marketingFieldClasses({ size: 'lg' })}
                  />
                </div>
              </div>

              {/* Intent picker — writes the same `subject` the select used to,
                  so lead scoring and the notification subject are unchanged. */}
              <div className='my-[30px]'>
                <span
                  className='text-sm font-semibold text-foreground-soft'
                  id='intent-label'
                >
                  What brings you here? *
                </span>
                <div
                  role='group'
                  aria-labelledby='intent-label'
                  className='grid grid-cols-2 md:grid-cols-3 gap-[7px] mt-2.5'
                >
                  {INTENTS.map((option, i) => (
                    <button
                      key={option.subject}
                      type='button'
                      aria-pressed={i === intent}
                      onClick={() => selectIntent(i)}
                      className={cn(
                        'flex flex-col justify-between items-start min-h-[86px] p-3.5 text-left border transition-colors',
                        i === intent
                          ? 'accent-surface border-[var(--cta-fill)]'
                          : 'border-line bg-card hover:border-line-strong'
                      )}
                    >
                      <strong className='text-sm font-semibold'>
                        {option.title}
                      </strong>
                      <small
                        className={cn(
                          'instrument-label',
                          i === intent ? '' : 'text-muted-foreground'
                        )}
                      >
                        {option.kicker}
                      </small>
                    </button>
                  ))}
                </div>
                <input
                  type='hidden'
                  id='subject'
                  name='subject'
                  value={formData.subject}
                />
                <div className='grid sm:grid-cols-[100px_1fr] gap-4 items-center min-h-[68px] mt-2 p-3.5 border border-line bg-card'>
                  <span className='instrument-label text-cta-ink'>
                    Starting point
                  </span>
                  <p
                    aria-live='polite'
                    className='m-0 text-xs leading-relaxed text-muted-foreground'
                  >
                    {intentContext}
                  </p>
                </div>
              </div>

              <div className='grid gap-2'>
                <label
                  htmlFor='budget'
                  className='text-sm font-semibold text-foreground-soft'
                >
                  Working budget{' '}
                  <span className='font-normal text-muted-foreground'>
                    (optional)
                  </span>
                </label>
                <select
                  id='budget'
                  name='budget'
                  value={formData.budget}
                  onChange={handleChange}
                  className={marketingFieldClasses({ size: 'lg' })}
                >
                  <option value=''>Select a range</option>
                  {BUDGETS.map(range => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>

              <div className='grid gap-2 mt-[25px]'>
                <div className='flex justify-between gap-4'>
                  <label
                    htmlFor='message'
                    className='text-sm font-semibold text-foreground-soft'
                  >
                    What are you trying to change? *
                  </label>
                  <span className='instrument-label text-muted-foreground normal-case tracking-normal'>
                    {formData.message.length} / {MESSAGE_MAX}
                  </span>
                </div>
                <textarea
                  id='message'
                  name='message'
                  required
                  maxLength={MESSAGE_MAX}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={activeIntent.placeholder}
                  className={cn(
                    marketingFieldClasses(),
                    'min-h-[165px] leading-relaxed resize-y'
                  )}
                />
              </div>

              <label className='grid grid-cols-[18px_1fr] gap-2.5 my-5 text-xs leading-relaxed text-muted-foreground'>
                <input
                  type='checkbox'
                  required
                  className='w-4 h-4 accent-[var(--cta-fill)]'
                />
                {/* Legal wording is the pre-existing sentence, verbatim. The
                    design reference shortened it to "Terms"; consent copy is
                    not a design decision. */}
                <span>
                  By submitting, you agree to our{' '}
                  <a
                    href='/terms-of-service'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-foreground-soft underline underline-offset-[3px]'
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href='/privacy-policy'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-foreground-soft underline underline-offset-[3px]'
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              {error && (
                <p
                  ref={errorRef}
                  role='alert'
                  tabIndex={-1}
                  className='card-copy border border-destructive/50 bg-destructive/10 px-4 py-3 text-destructive mb-5'
                >
                  {error}
                </p>
              )}

              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5'>
                <p className='max-w-[330px] m-0 text-xs leading-relaxed text-muted-foreground'>
                  You will not be added to a marketing list. We will use this
                  information only to respond to your inquiry.
                </p>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  variant='action'
                  className='group shrink-0'
                >
                  Send project brief
                  <Arrow />
                </Button>
              </div>
            </form>
          </div>
        </Section>
      </div>

      {/* ── What happens next */}
      <Section width='wide'>
        <div className='flex flex-col lg:flex-row lg:justify-between lg:items-end gap-8 lg:gap-[50px] mb-[52px]'>
          <div>
            <span className='instrument-label block mb-5'>
              What happens next
            </span>
            <h2 className='thesis-headline'>No endless discovery sequence.</h2>
          </div>
          <p className='section-lead lg:max-w-[470px]'>
            A short path from your brief to a scoped decision.
          </p>
        </div>

        <div className='grid md:grid-cols-3 gap-px border border-line bg-line'>
          {NEXT_STEPS.map((step, i) => (
            <article
              key={step.number}
              className='relative min-h-[315px] p-7 bg-card'
            >
              <span className='instrument-label text-cta-ink'>
                {step.number}
              </span>
              <h3 className='card-headline mt-[85px]'>{step.title}</h3>
              <p className='m-0 mt-3 text-xs leading-relaxed text-muted-foreground'>
                {step.copy}
              </p>
              <span
                aria-hidden='true'
                className={cn(
                  'absolute top-6 right-6 font-mono',
                  i === NEXT_STEPS.length - 1
                    ? 'text-cta'
                    : 'text-muted-foreground'
                )}
              >
                {i === NEXT_STEPS.length - 1 ? '✓' : '→'}
              </span>
            </article>
          ))}
        </div>
      </Section>

      {/* ── Fit */}
      <div className='border-t border-line'>
        <Section width='wide'>
          <div className='grid lg:grid-cols-2 gap-px border border-line-strong bg-line'>
            <div className='accent-surface min-h-[430px] p-[clamp(34px,5vw,60px)]'>
              <span className='instrument-label block mb-5'>A strong fit</span>
              <h2 className='thesis-headline'>
                You need one team to own the outcome.
              </h2>
              <ul className='grid gap-3.5 mt-[30px] list-none m-0 p-0'>
                {STRONG_FIT.map(item => (
                  <li
                    key={item}
                    className='grid grid-cols-[18px_1fr] gap-2.5 m-0 text-sm leading-relaxed text-white/78'
                  >
                    <span aria-hidden='true' className='font-extrabold'>
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className='min-h-[430px] p-[clamp(34px,5vw,60px)] bg-card'>
              <span className='instrument-label block mb-5'>Start smaller</span>
              <h2 className='thesis-headline'>
                You need evidence before an engagement.
              </h2>
              <ul className='grid gap-3.5 mt-[30px] list-none m-0 p-0'>
                {START_SMALLER.map(item => (
                  <li
                    key={item}
                    className='grid grid-cols-[18px_1fr] gap-2.5 m-0 text-sm leading-relaxed text-muted-foreground'
                  >
                    <span
                      aria-hidden='true'
                      className='text-cta font-extrabold'
                    >
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className='mt-7'>
                <LinkButton
                  href='/platform'
                  variant='neutral'
                  className='group'
                  data-cta-id='contact_radar'
                >
                  Get My Free Snapshot
                  <Arrow />
                </LinkButton>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ── FAQ */}
      <Section width='wide'>
        <div className='grid lg:grid-cols-[0.72fr_1.28fr] gap-10 lg:gap-[75px]'>
          <div>
            <span className='instrument-label block mb-5'>
              Before you write
            </span>
            <h2 className='thesis-headline'>A few useful answers.</h2>
            <p className='section-lead mt-6'>
              Enough to know whether the form is the right next move.
            </p>
          </div>
          <div className='border-t border-line'>
            {FAQS.map(faq => (
              <details key={faq.q} className='group border-b border-line'>
                <summary className='flex justify-between gap-8 py-6 font-display text-lg font-semibold tracking-[-0.025em] cursor-pointer list-none [&::-webkit-details-marker]:hidden'>
                  {faq.q}
                  <span aria-hidden='true' className='text-cta shrink-0'>
                    <span className='group-open:hidden'>+</span>
                    <span className='hidden group-open:inline'>−</span>
                  </span>
                </summary>
                <p className='mt-[-2px] mr-[35px] mb-6 text-sm leading-loose text-muted-foreground'>
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}
